/*
 * СЦЕНЫ С ИНТЕРФЕЙСОМ РЕДАКТОРА — «покажи, куда нажать».
 *
 * ЗАЧЕМ ОНИ. Анимации из CadDemos показывают, ЧТО происходит с деталью:
 * эскиз поднимается в объём, сверлится отверстие. Но ребёнок, впервые открывший
 * редактор, спрашивает другое — ГДЕ КНОПКА. Записать экранное видео нельзя
 * (курсор будет метаться, интерфейс поменяется — и ролик протухнет), поэтому
 * рисуем схему редактора своими руками: панель инструментов сверху, вьюпорт
 * с сеткой, курсор, который наводится и кликает, и результат прямо в сцене.
 *
 * СХЕМА, А НЕ КОПИЯ. Панель повторяет реальную структуру (группы ЭСКИЗ ·
 * СОЗДАТЬ · ИЗМЕНИТЬ, зелёная кнопка эскиза, синие операции), но не пиксель
 * в пиксель: точная копия развалится при первой же правке интерфейса,
 * а узнаваемая схема переживёт её.
 *
 * АНИМАЦИЯ — та же техника, что в CadDemos: чистый SMIL внутри SVG, без
 * таймеров и состояния React. Кадр крутится сам, сколько бы ни висел на экране.
 */

import React from 'react';
import cl from './CadDemos.module.css';


/* ── Палитра схемы: те же токены, что в самом редакторе ─────────────────── */
const C = {
    chrome: '#1a1d25',      // фон панелей
    chrome2: '#21252f',     // кнопки
    line: '#313745',        // границы
    ink: '#eef1f6',         // текст
    inkMute: '#8b93a5',     // подписи
    accent: '#38d9f0',      // акцент (подсветка)
    sketch: '#6dc36d',      // зелёный — эскиз (как в TopBar)
    create: '#5fb8e7',      // синий — создать/выдавить
    modify: '#e8b73a',      // жёлтый — изменить
    viewport: '#0f1116',    // фон сцены
    grid: '#252a34',        // сетка
    part: '#c99150',        // деталь
    partTop: '#e8b877',
    partSide: '#a97a3c',
};


/** Кнопка панели инструментов: квадрат с «иконкой» и подписью. */
function ToolBtn({ x, y, label, color, w = 17, highlight }) {
    return (
        <g>
            <rect x={x} y={y} width={w} height="13" rx="1.6"
                fill={highlight ? 'rgba(56,217,240,.16)' : C.chrome2}
                stroke={highlight ? C.accent : C.line}
                strokeWidth={highlight ? '0.7' : '0.35'}/>
            {/* «Иконка» — цветной значок операции. */}
            <rect x={x + w / 2 - 2.6} y={y + 2} width="5.2" height="5.2" rx="1"
                fill={color} opacity={highlight ? 1 : 0.85}/>
            {/* Подпись мельче ширины кнопки: длинные слова («Скругление»,
                «Отверстие») иначе вылезают за края и наезжают на соседей. */}
            <text x={x + w / 2} y={y + 11} textAnchor="middle"
                fontSize={label.length > 8 ? '2.3' : '2.8'}
                fill={highlight ? C.ink : C.inkMute}>{label}</text>
        </g>
    );
}


/** Заголовок группы инструментов под кнопками — как в реальном тулбаре. */
function GroupLabel({ x, y, w, text }) {
    return (
        <>
            <line x1={x} y1={y - 1.4} x2={x + w} y2={y - 1.4}
                stroke={C.line} strokeWidth="0.3"/>
            <text x={x + w / 2} y={y + 2} textAnchor="middle" fontSize="2.5"
                fill={C.inkMute} letterSpacing="0.3">{text}</text>
        </>
    );
}


/** Курсор-стрелка. Двигается анимацией родителя. */
function Cursor({ x = 0, y = 0 }) {
    return (
        <g transform={`translate(${x} ${y})`}>
            <path d="M0,0 L0,7.2 L1.9,5.6 L3.1,8.4 L4.6,7.7 L3.4,5 L5.8,4.9 Z"
                fill={C.ink} stroke="#0b0d11" strokeWidth="0.4" strokeLinejoin="round"/>
        </g>
    );
}


/** Кольцо-«клик» — расходится в момент нажатия. */
function ClickRing({ x, y, begin, dur = '4s' }) {
    return (
        <circle cx={x} cy={y} r="2" fill="none" stroke={C.accent} strokeWidth="0.8" opacity="0">
            <animate attributeName="r" values="1;7" dur="0.55s"
                begin={begin} repeatCount="indefinite" fill="freeze"/>
            <animate attributeName="opacity" values="0;0.9;0" dur="0.55s"
                begin={begin} repeatCount="indefinite"/>
        </circle>
    );
}


/**
 * Каркас окна редактора: панель инструментов сверху, вьюпорт с сеткой.
 * Дети сцены рисуются ВНУТРИ вьюпорта (координаты — общие, viewBox 100×56).
 */
function EditorFrame({ tools, children }) {
    return (
        <>
            {/* Окно */}
            <rect x="1" y="1" width="98" height="54" rx="2"
                fill={C.chrome} stroke={C.line} strokeWidth="0.4"/>

            {/* Панель инструментов */}
            <rect x="1" y="1" width="98" height="18" fill={C.chrome}/>
            <line x1="1" y1="19" x2="99" y2="19" stroke={C.line} strokeWidth="0.4"/>
            {tools}

            {/* Вьюпорт со светлой сеткой — как в реальном редакторе */}
            <rect x="1" y="19" width="98" height="36" fill={C.viewport}/>
            <g stroke={C.grid} strokeWidth="0.25">
                {[24, 30, 36, 42, 48].map((y) => (
                    <line key={y} x1="1" y1={y} x2="99" y2={y}/>
                ))}
                {[10, 22, 34, 46, 58, 70, 82, 94].map((x) => (
                    <line key={x} x1={x} y1="19" x2={x} y2="55"/>
                ))}
            </g>

            {children}
        </>
    );
}


/** Стандартный набор кнопок панели. active — какую подсветить. */
function Toolbar({ active }) {
    return (
        <>
            <ToolBtn x={4} y={3} label="Эскиз" color={C.sketch}
                highlight={active === 'sketch'}/>
            <GroupLabel x={4} y={17} w={17} text="ЭСКИЗ"/>

            <ToolBtn x={25} y={3} label="Выдавить" color={C.create} w={19}
                highlight={active === 'extrude'}/>
            <ToolBtn x={46} y={3} label="Отверстие" color={C.create} w={19}
                highlight={active === 'hole'}/>
            <GroupLabel x={25} y={17} w={40} text="СОЗДАТЬ"/>

            <ToolBtn x={69} y={3} label="Скругление" color={C.modify} w={14}
                highlight={active === 'fillet'}/>
            <ToolBtn x={84} y={3} label="Оболочка" color={C.modify} w={12}
                highlight={active === 'shell'}/>
            <GroupLabel x={69} y={17} w={27} text="ИЗМЕНИТЬ"/>
        </>
    );
}


/** Обёртка сцены: тот же контракт, что у CadDemos (Scene). */
function UiScene({ caption, children, bare }) {
    const svgRef = React.useRef(null);

    React.useEffect(() => {
        const svg = svgRef.current;
        if (!svg || typeof svg.pauseAnimations !== 'function') return;
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const apply = () => {
            if (media.matches) svg.pauseAnimations();
            else svg.unpauseAnimations();
        };
        apply();
        media.addEventListener('change', apply);
        return () => media.removeEventListener('change', apply);
    }, []);

    return (
        <figure className={bare ? cl.wrapBare : cl.wrap}>
            <svg ref={svgRef} viewBox="0 0 100 56"
                className={bare ? cl.svgBare : cl.svg}
                preserveAspectRatio="xMidYMid meet" role="img" aria-label={caption}>
                {children}
            </svg>
            {!bare && <figcaption className={cl.caption}>{caption}</figcaption>}
        </figure>
    );
}


/* ═══ 1. ОБЗОР ОКНА: что где находится ═════════════════════════════════ */
function UiOverview({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Окно редактора: сверху инструменты, в центре сцена, где живёт деталь">
            <EditorFrame tools={<Toolbar/>}>
                {/* Оси в центре сцены */}
                <line x1="34" y1="42" x2="62" y2="42" stroke="#c0504d" strokeWidth="0.5"/>
                <line x1="34" y1="42" x2="46" y2="34" stroke="#4f9a4f" strokeWidth="0.5"/>
                <line x1="34" y1="42" x2="34" y2="28" stroke="#4a7ec0" strokeWidth="0.5"/>
            </EditorFrame>

            {/* Поочерёдная подсветка зон: сначала панель, потом сцена */}
            <rect x="2" y="2" width="96" height="16" rx="1.5" fill="none"
                stroke={C.accent} strokeWidth="0.7" opacity="0">
                <animate attributeName="opacity" values="0;1;1;0;0"
                    keyTimes="0;0.08;0.4;0.48;1" dur="6s" repeatCount="indefinite"/>
            </rect>
            {/* Подпись зоны — ПОД панелью, на поле сцены: поверх кнопок она
                перекрывала то, что как раз и надо разглядеть. */}
            <text x="50" y="24.5" textAnchor="middle" fontSize="3.4" fontWeight="700"
                fill={C.accent} opacity="0">
                это инструменты
                <animate attributeName="opacity" values="0;1;1;0;0"
                    keyTimes="0;0.08;0.4;0.48;1" dur="6s" repeatCount="indefinite"/>
            </text>

            <rect x="2" y="20" width="96" height="34" rx="1.5" fill="none"
                stroke={C.accent} strokeWidth="0.7" opacity="0">
                <animate attributeName="opacity" values="0;0;1;1;0"
                    keyTimes="0;0.5;0.58;0.9;1" dur="6s" repeatCount="indefinite"/>
            </rect>
            <text x="50" y="51" textAnchor="middle" fontSize="3.2" fontWeight="700"
                fill={C.accent} opacity="0">
                здесь живёт деталь
                <animate attributeName="opacity" values="0;0;1;1;0"
                    keyTimes="0;0.5;0.58;0.9;1" dur="6s" repeatCount="indefinite"/>
            </text>
        </UiScene>
    );
}


/* ═══ 2. ВРАЩЕНИЕ СЦЕНЫ мышью ═════════════════════════════════════════ */
function UiOrbit({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Зажми правую кнопку мыши и веди — сцена повернётся вокруг детали">
            <EditorFrame tools={<Toolbar/>}>
                {/* Деталь поворачивается: имитируем сжатием по горизонтали
                    (в изометрии поворот читается именно так). */}
                <g>
                    <animateTransform attributeName="transform" type="scale"
                        values="1 1; 0.55 1; 1 1; 0.55 1; 1 1" dur="6s"
                        additive="sum" repeatCount="indefinite"/>
                    <g transform="translate(50 0)">
                        <g transform="translate(-50 0)">
                            <polygon points="38,34 56,30 68,35 50,39"
                                fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3"/>
                            <polygon points="38,34 50,39 50,48 38,43"
                                fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                            <polygon points="50,39 68,35 68,44 50,48"
                                fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>
                        </g>
                    </g>
                </g>
            </EditorFrame>

            {/* Курсор с зажатой правой кнопкой ездит влево-вправо */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="62,40; 38,36; 62,40" dur="6s" repeatCount="indefinite"/>
                <Cursor/>
                {/* Значок зажатой кнопки — маленькая мышь рядом с курсором */}
                <rect x="6" y="-1" width="5" height="7" rx="2.4" fill="none"
                    stroke={C.accent} strokeWidth="0.5"/>
                <path d="M8.5,-1 v3.4 h2.5 v-1 a2.4,2.4 0 0 0 -2.5,-2.4 z" fill={C.accent}/>
            </g>
        </UiScene>
    );
}


/* ═══ 3. СОЗДАТЬ ЭСКИЗ: клик по кнопке → выбор плоскости ═══════════════ */
function UiNewSketch({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Нажми «Эскиз» в левой группе панели, потом щёлкни по плоскости, на которой рисуешь">
            <EditorFrame tools={<Toolbar active="sketch"/>}>
                {/* Три плоскости; нужная подсвечивается после клика по кнопке */}
                <polygon points="30,40 50,34 70,40 50,46"
                    fill="rgba(56,217,240,.10)" stroke={C.accent}
                    strokeWidth="0.4" strokeDasharray="1.5 1">
                    <animate attributeName="fill-opacity" values="0.1;0.1;0.34;0.34"
                        keyTimes="0;0.45;0.6;1" dur="4s" repeatCount="indefinite"/>
                </polygon>
                <text x="50" y="51" textAnchor="middle" fontSize="3" fill={C.inkMute}>
                    плоскость для эскиза
                </text>
            </EditorFrame>

            {/* Курсор едет к кнопке «Эскиз», кликает, потом идёт на плоскость */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="60,40; 11,9; 11,9; 50,40; 50,40; 60,40"
                    keyTimes="0;0.22;0.42;0.62;0.85;1"
                    dur="4s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={11} y={9} begin="0.9s"/>
            <ClickRing x={50} y={40} begin="2.5s"/>
        </UiScene>
    );
}


/* ═══ 4. РИСУЕМ ПРЯМОУГОЛЬНИК И СТАВИМ РАЗМЕР ═════════════════════════ */
function UiSketchRect({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Протяни прямоугольник мышью, а потом впиши точный размер — на глаз рисовать нельзя">
            <EditorFrame tools={<Toolbar active="sketch"/>}>
                {/* Прямоугольник растягивается за курсором */}
                <rect x="32" y="30" width="0" height="0" fill="rgba(56,217,240,.12)"
                    stroke={C.accent} strokeWidth="0.6">
                    <animate attributeName="width" values="0;34;34;34;0"
                        keyTimes="0;0.35;0.9;0.97;1" dur="5.5s" repeatCount="indefinite"/>
                    <animate attributeName="height" values="0;16;16;16;0"
                        keyTimes="0;0.35;0.9;0.97;1" dur="5.5s" repeatCount="indefinite"/>
                </rect>

                {/* Поле ввода размера — появляется после того, как фигура готова */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.42;0.5;0.92;1" dur="5.5s" repeatCount="indefinite"/>
                    <rect x="30" y="49" width="20" height="6.4" rx="1.2"
                        fill={C.chrome2} stroke={C.accent} strokeWidth="0.5"/>
                    <text x="33" y="53.5" fontSize="2.8" fill={C.inkMute}>длина</text>
                    <text x="47" y="53.7" textAnchor="end" fontSize="3.8"
                        fontFamily="monospace" fill={C.ink}>40</text>
                </g>

                {/* Размерная линия под фигурой */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.42;0.5;0.92;1" dur="5.5s" repeatCount="indefinite"/>
                    <line x1="32" y1="47" x2="66" y2="47" stroke={C.accent} strokeWidth="0.4"/>
                    <line x1="32" y1="45.6" x2="32" y2="48.4" stroke={C.accent} strokeWidth="0.4"/>
                    <line x1="66" y1="45.6" x2="66" y2="48.4" stroke={C.accent} strokeWidth="0.4"/>
                </g>
            </EditorFrame>

            {/* Курсор тянет угол прямоугольника, затем идёт к полю ввода */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="32,30; 66,46; 66,46; 52,53; 52,53; 32,30"
                    keyTimes="0;0.35;0.44;0.55;0.92;1"
                    dur="5.5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
        </UiScene>
    );
}


/* ═══ 5. ВЫХОД ИЗ ЭСКИЗА И ВЫДАВЛИВАНИЕ ═══════════════════════════════ */
function UiExtrudeFlow({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Закрой эскиз, нажми «Выдавить» и задай высоту — плоская фигура станет деталью">
            <EditorFrame tools={<Toolbar active="extrude"/>}>
                {/* Плоский эскиз лежит на месте */}
                <polygon points="34,42 54,37 70,42 50,47"
                    fill="none" stroke={C.accent} strokeWidth="0.5" strokeDasharray="1.5 1"/>

                {/* Тело растёт вверх после клика по «Выдавить» */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.36;0.44;0.92;1" dur="5s" repeatCount="indefinite"/>
                    <g>
                        <animateTransform attributeName="transform" type="scale"
                            values="1 0.06; 1 0.06; 1 1; 1 1; 1 0.06"
                            keyTimes="0;0.36;0.62;0.92;1" dur="5s"
                            additive="sum" repeatCount="indefinite"/>
                        <g transform="translate(0 47)">
                            <g transform="translate(0 -47)">
                                <polygon points="34,32 54,27 70,32 50,37"
                                    fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3"/>
                                <polygon points="34,32 50,37 50,47 34,42"
                                    fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                                <polygon points="50,37 70,32 70,42 50,47"
                                    fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>
                            </g>
                        </g>
                    </g>
                </g>

                {/* Поле высоты */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.4;0.5;0.92;1" dur="5s" repeatCount="indefinite"/>
                    <rect x="72" y="23" width="24" height="7" rx="1.2"
                        fill={C.chrome2} stroke={C.accent} strokeWidth="0.5"/>
                    <text x="74.5" y="27.8" fontSize="2.8" fill={C.inkMute}>высота</text>
                    <text x="93.5" y="28" textAnchor="end" fontSize="3.8"
                        fontFamily="monospace" fill={C.ink}>20</text>
                </g>
            </EditorFrame>

            {/* Курсор кликает «Выдавить», потом вводит высоту */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="60,44; 34,9; 34,9; 84,30; 84,30; 60,44"
                    keyTimes="0;0.2;0.36;0.52;0.9;1"
                    dur="5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={34} y={9} begin="1s" dur="5s"/>
        </UiScene>
    );
}


/* ═══ 6. ОТВЕРСТИЕ: выбрать грань → нажать «Отверстие» ════════════════ */
function UiHoleFlow({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Щёлкни по грани, нажми «Отверстие» и задай диаметр — вырез появится там, где выбрал">
            <EditorFrame tools={<Toolbar active="hole"/>}>
                {/* Деталь */}
                <polygon points="34,32 54,27 70,32 50,37"
                    fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="34,32 50,37 50,47 34,42"
                    fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="50,37 70,32 70,42 50,47"
                    fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>

                {/* Подсветка выбранной верхней грани */}
                <polygon points="34,32 54,27 70,32 50,37" fill={C.accent} opacity="0">
                    <animate attributeName="opacity" values="0;0.34;0.34;0;0"
                        keyTimes="0;0.18;0.5;0.58;1" dur="5s" repeatCount="indefinite"/>
                </polygon>

                {/* Отверстие «просверливается» */}
                <ellipse cx="52" cy="32.5" rx="3.4" ry="1.7" fill="#1a130c" opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.55;0.66;0.92;1" dur="5s" repeatCount="indefinite"/>
                    <animate attributeName="ry" values="0.2;0.2;1.7;1.7;0.2"
                        keyTimes="0;0.55;0.66;0.92;1" dur="5s" repeatCount="indefinite"/>
                </ellipse>
            </EditorFrame>

            {/* Курсор: грань → кнопка «Отверстие» */}
            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="66,44; 52,32; 52,32; 55,9; 55,9; 66,44"
                    keyTimes="0;0.16;0.34;0.52;0.9;1"
                    dur="5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={52} y={32} begin="1s" dur="5s"/>
            <ClickRing x={55} y={9} begin="2.7s" dur="5s"/>
        </UiScene>
    );
}


/* ═══ 7. СКРУГЛЕНИЕ: выбрать ребро → задать радиус ═══════════════════ */
function UiFilletFlow({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Выбери ребро, нажми «Скругление» и задай радиус — острый угол станет круглым">
            <EditorFrame tools={<Toolbar active="fillet"/>}>
                <polygon points="34,32 54,27 70,32 50,37"
                    fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="34,32 50,37 50,47 34,42"
                    fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="50,37 70,32 70,42 50,47"
                    fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>

                {/* Выбранное ребро подсвечивается */}
                <line x1="50" y1="37" x2="50" y2="47" stroke={C.accent} strokeWidth="1.4"
                    opacity="0">
                    <animate attributeName="opacity" values="0;1;1;0;0"
                        keyTimes="0;0.2;0.55;0.65;1" dur="5s" repeatCount="indefinite"/>
                </line>

                {/* Скругление на месте ребра */}
                <path d="M47,36 q3,5.5 0,11" fill="none" stroke={C.sketch}
                    strokeWidth="1.2" opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.6;0.7;0.92;1" dur="5s" repeatCount="indefinite"/>
                </path>

                {/* Поле радиуса */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.55;0.66;0.92;1" dur="5s" repeatCount="indefinite"/>
                    <rect x="72" y="23" width="24" height="7" rx="1.2"
                        fill={C.chrome2} stroke={C.accent} strokeWidth="0.5"/>
                    <text x="74.5" y="27.8" fontSize="2.8" fill={C.inkMute}>радиус</text>
                    <text x="93.5" y="28" textAnchor="end" fontSize="3.8"
                        fontFamily="monospace" fill={C.ink}>4</text>
                </g>
            </EditorFrame>

            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="64,44; 50,42; 50,42; 75,9; 75,9; 64,44"
                    keyTimes="0;0.18;0.36;0.54;0.9;1"
                    dur="5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={50} y={42} begin="1.1s" dur="5s"/>
            <ClickRing x={75} y={9} begin="2.8s" dur="5s"/>
        </UiScene>
    );
}


/* ═══ 8. ОБОЛОЧКА: выбрать грань → задать толщину ════════════════════ */
function UiShellFlow({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Выбери верхнюю грань и нажми «Оболочка» — деталь станет коробкой со стенками">
            <EditorFrame tools={<Toolbar active="shell"/>}>
                <polygon points="34,32 54,27 70,32 50,37"
                    fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3">
                    <animate attributeName="opacity" values="1;1;0;0;1"
                        keyTimes="0;0.55;0.65;0.92;1" dur="5.5s" repeatCount="indefinite"/>
                </polygon>
                <polygon points="34,32 50,37 50,47 34,42"
                    fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="50,37 70,32 70,42 50,47"
                    fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>

                {/* Подсветка выбираемой грани */}
                <polygon points="34,32 54,27 70,32 50,37" fill={C.accent} opacity="0">
                    <animate attributeName="opacity" values="0;0.34;0.34;0;0"
                        keyTimes="0;0.16;0.5;0.6;1" dur="5.5s" repeatCount="indefinite"/>
                </polygon>

                {/* Полость: дно + кромка стенки */}
                <g opacity="0">
                    <animate attributeName="opacity" values="0;0;1;1;0"
                        keyTimes="0;0.6;0.7;0.92;1" dur="5.5s" repeatCount="indefinite"/>
                    <polygon points="37,32.3 54,28.2 67,32.3 50,36.4" fill="#1a130c"/>
                    <polygon points="37,32.3 54,28.2 67,32.3 50,36.4"
                        fill="none" stroke={C.sketch} strokeWidth="0.6"/>
                </g>
            </EditorFrame>

            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="66,44; 52,32; 52,32; 90,9; 90,9; 66,44"
                    keyTimes="0;0.16;0.34;0.52;0.9;1"
                    dur="5.5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={52} y={32} begin="1.1s" dur="5.5s"/>
            <ClickRing x={90} y={9} begin="3s" dur="5.5s"/>
        </UiScene>
    );
}


/* ═══ 9. КУБ-НАВИГАТОР: встать на нужный вид ═════════════════════════ */
function UiViewCube({ bare }) {
    return (
        <UiScene bare={bare}
            caption="Куб-навигатор в углу: щёлкни по его грани — камера встанет ровно спереди или сверху">
            <EditorFrame tools={<Toolbar/>}>
                <polygon points="36,34 54,29 68,34 50,39"
                    fill={C.partTop} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="36,34 50,39 50,48 36,43"
                    fill={C.partSide} stroke="#6b4a1f" strokeWidth="0.3"/>
                <polygon points="50,39 68,34 68,43 50,48"
                    fill={C.part} stroke="#6b4a1f" strokeWidth="0.3"/>

                {/* Сам куб-навигатор в правом верхнем углу вьюпорта */}
                <g>
                    <polygon points="84,22 91,20 96,22.5 89,24.5"
                        fill={C.chrome2} stroke={C.inkMute} strokeWidth="0.35"/>
                    <polygon points="84,22 89,24.5 89,30 84,27.5"
                        fill={C.chrome} stroke={C.inkMute} strokeWidth="0.35"/>
                    <polygon points="89,24.5 96,22.5 96,28 89,30"
                        fill={C.chrome2} stroke={C.inkMute} strokeWidth="0.35"/>
                    {/* Верхняя грань подсвечивается при «клике» */}
                    <polygon points="84,22 91,20 96,22.5 89,24.5" fill={C.accent} opacity="0">
                        <animate attributeName="opacity" values="0;0;0.7;0.7;0"
                            keyTimes="0;0.35;0.45;0.75;1" dur="5s" repeatCount="indefinite"/>
                    </polygon>
                    <text x="90" y="34" textAnchor="middle" fontSize="2.8"
                        fill={C.inkMute}>навигатор</text>
                </g>
            </EditorFrame>

            <g>
                <animateTransform attributeName="transform" type="translate"
                    values="60,44; 90,22; 90,22; 60,44"
                    keyTimes="0;0.35;0.78;1" dur="5s" repeatCount="indefinite"/>
                <Cursor/>
            </g>
            <ClickRing x={90} y={22} begin="1.8s" dur="5s"/>
        </UiScene>
    );
}


/** Виды сцен с интерфейсом. */
export const UI_DEMO_KINDS = new Set([
    'ui-overview', 'ui-orbit', 'ui-sketch', 'ui-rect',
    'ui-extrude', 'ui-hole', 'ui-fillet', 'ui-shell', 'ui-viewcube',
]);

/** Показать сцену с интерфейсом по её виду. */
const UiDemo = ({ kind, bare }) => {
    switch (kind) {
        case 'ui-overview': return <UiOverview bare={bare}/>;
        case 'ui-orbit':    return <UiOrbit bare={bare}/>;
        case 'ui-sketch':   return <UiNewSketch bare={bare}/>;
        case 'ui-rect':     return <UiSketchRect bare={bare}/>;
        case 'ui-extrude':  return <UiExtrudeFlow bare={bare}/>;
        case 'ui-hole':     return <UiHoleFlow bare={bare}/>;
        case 'ui-fillet':   return <UiFilletFlow bare={bare}/>;
        case 'ui-shell':    return <UiShellFlow bare={bare}/>;
        case 'ui-viewcube': return <UiViewCube bare={bare}/>;
        default:            return null;
    }
};

export default UiDemo;
